import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { UserType } from "@/constants";
import { useUserForm } from "@/hooks/use-user-form";
import { governmentImpersonatePortalHref } from "@/lib/government-impersonate";
import {
  getQueryErrorHeading,
  getQueryErrorMessage,
  getUserType,
} from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { ExternalLink } from "lucide-react";
import ErrorComponent from "../ui/error";
import LoadingScreen from "../ui/loading-screen";
import { UserForm } from "./user-form";

interface UserDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  /** Show Impersonate that opens the government portal as this user. Operator portal only. */
  showImpersonateButton?: boolean;
  /** Operator admins can create operators and set Admin and Sales. */
  canManageOperators?: boolean;
}

export function UserDetailsSheet({
  isOpen,
  onOpenChange,
  userId,
  showImpersonateButton = false,
  canManageOperators = false,
}: UserDetailsSheetProps) {
  const { user } = useAuth0();
  const { userData, jurisdictionsData, isLoading, error, handleSubmit } =
    useUserForm(userId);

  const handleCancel = () => {
    onOpenChange(false);
  };

  const userType = getUserType(user);
  const canImpersonate =
    showImpersonateButton &&
    Boolean(userId) &&
    userData?.user_type === UserType.GOVERNMENT;

  return (
    (userType === UserType.OPERATOR || userType === UserType.GOVERNMENT) && (
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl">
          <SheetHeader>
            <div className="flex items-start justify-between gap-2 pr-8">
              <div>
                <SheetTitle>
                  {!userId ? "Add New User" : "Edit User"}
                </SheetTitle>
                <SheetDescription>
                  {!userId ? "Create a new user." : "Edit an existing user."}
                </SheetDescription>
              </div>
              {canImpersonate && userId ? (
                <Button
                  size="sm"
                  variant="outline"
                  aria-label="Impersonate government user"
                  onClick={() =>
                    window.open(
                      governmentImpersonatePortalHref(userId),
                      "_blank",
                    )
                  }
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Impersonate
                </Button>
              ) : null}
            </div>
          </SheetHeader>
          <div className="overflow-y-auto max-h-[calc(100vh-120px)] px-4">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <LoadingScreen />
              </div>
            ) : error ? (
              <ErrorComponent
                title={getQueryErrorHeading(error)}
                message={getQueryErrorMessage(error)}
              />
            ) : (
              <UserForm
                userId={userId}
                userData={userData}
                currentUserType={userType}
                canManageOperators={canManageOperators}
                jurisdictions={jurisdictionsData}
                onSubmit={async (data) => {
                  try {
                    await handleSubmit(data);
                    onOpenChange(false);
                  } catch (error) {
                    // Error is already handled with toast in the hook
                  }
                }}
                onCancel={handleCancel}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    )
  );
}
